import type { Lead } from '@up2stay/up2promo-api-client';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { Contact, Download } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  triggerErrorHaptic,
  triggerSuccessHaptic,
  triggerTapHaptic,
} from '@/core/lib/dnd-haptics';
import { leadHasVcfContent } from '@/core/lib/leads-vcf';
import { saveLeadToDevice, shareLeadVcf } from '@/core/lib/save-lead-to-device';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

export type LeadSaveToDeviceLabels = {
  menuLabel: string;
  addToContacts: string;
  downloadVCard: string;
  shareVCard: string;
  toastContactsSaved: string;
  toastEmpty: string;
  toastFailed: string;
};

interface LeadSaveToDeviceMenuProps {
  lead: Lead;
  labels: LeadSaveToDeviceLabels;
}

export function LeadSaveToDeviceMenu({
  lead,
  labels,
}: LeadSaveToDeviceMenuProps) {
  const [busy, setBusy] = useState(false);
  const native = isCapacitorNative();
  const canSave = leadHasVcfContent(lead);

  const run = async (action: 'contacts' | 'vcf') => {
    if (busy || !canSave) {
      if (!canSave) toast.error(labels.toastEmpty);
      return;
    }

    void triggerTapHaptic();
    setBusy(true);
    try {
      const result =
        action === 'contacts'
          ? await saveLeadToDevice(lead)
          : await shareLeadVcf(lead);

      if (result.outcome === 'empty') {
        void triggerErrorHaptic();
        toast.error(labels.toastEmpty);
        return;
      }

      // Native draft: user already confirmed via the system Done button.
      // Share sheet / browser download already confirm vCard export.
      // Cancelled: no toast.
      if (result.outcome === 'contacts') {
        void triggerSuccessHaptic();
      }
    } catch (error) {
      console.error('Save lead to device failed', error);
      void triggerErrorHaptic();
      toast.error(labels.toastFailed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="md"
          className="h-9 gap-[7px] bg-transparent px-3.5 text-[13px] font-semibold"
          disabled={busy}
          aria-label={labels.menuLabel}
        >
          <Contact className="size-[15px]" />
          <span className="hidden sm:inline">{labels.menuLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {native ? (
          <>
            <DropdownMenuItem
              disabled={busy || !canSave}
              onClick={() => void run('contacts')}
            >
              <Contact className="h-4 w-4" />
              {labels.addToContacts}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={busy || !canSave}
              onClick={() => void run('vcf')}
            >
              <Download className="h-4 w-4" />
              {labels.shareVCard}
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem
            disabled={busy || !canSave}
            onClick={() => void run('vcf')}
          >
            <Download className="h-4 w-4" />
            {labels.downloadVCard}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
